import React, { forwardRef } from 'react';

const variants = {
  primary: 'bg-accent text-surface-0 hover:bg-accent-hover active:bg-accent-strong focus-visible:ring-accent shadow-sm hover:shadow-[0_8px_24px_rgba(199,243,107,.12)]',
  secondary: 'bg-surface-3 text-text-primary border border-border-subtle hover:bg-surface-4 hover:border-border-default active:bg-surface-5 focus-visible:ring-accent',
  ghost: 'text-text-secondary hover:text-text-primary hover:bg-surface-3 active:bg-surface-4 focus-visible:ring-accent',
  danger: 'bg-error/10 text-error border border-error/20 hover:bg-error/20 hover:border-error/30 active:bg-error/30 focus-visible:ring-error',
  outline: 'border border-border-default text-text-primary hover:bg-surface-2 hover:border-border-strong active:bg-surface-3 focus-visible:ring-accent',
  link: 'text-accent hover:text-accent-hover underline-offset-4 hover:underline focus-visible:ring-accent p-0',
};

const sizes = {
  xs: 'h-7 px-2.5 text-xs gap-1.5 rounded-md',
  sm: 'h-8 px-3 text-sm gap-1.5 rounded-xl',
  md: 'h-10 px-4 text-sm gap-2 rounded-xl',
  lg: 'h-11 px-5 text-base gap-2 rounded-xl',
  xl: 'h-12 px-6 text-base gap-2.5 rounded-xl',
  icon: 'h-10 w-10 rounded-xl',
  'icon-sm': 'h-8 w-8 rounded-xl',
  'icon-xs': 'h-7 w-7 rounded-md',
};

const Button = forwardRef(({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  icon,
  iconRight,
  children,
  className = '',
  ...props
}, ref) => {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      disabled={isDisabled}
      className={`
        inline-flex items-center justify-center font-medium
        transition-all duration-fast ease-out
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0
        select-none whitespace-nowrap
        ${!isDisabled ? 'active:scale-[.97]' : ''}
        ${isDisabled ? 'opacity-50 pointer-events-none' : ''}
        ${variants[variant]}
        ${sizes[size]}
        ${className}
      `.replace(/\s+/g, ' ').trim()}
      aria-busy={loading}
      aria-disabled={isDisabled}
      {...props}
    >
      {loading ? (
        <svg className="animate-spin shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ) : icon ? (
        <span className="shrink-0 [&_svg]:w-4 [&_svg]:h-4">{icon}</span>
      ) : null}
      {children}
      {iconRight && !loading && (
        <span className="shrink-0 [&_svg]:w-4 [&_svg]:h-4">{iconRight}</span>
      )}
    </button>
  );
});

Button.displayName = 'Button';
export default Button;
